import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { RESIDENCES as DATA } from '../../data/content';
import { RESIDENCES } from '../../constants/testIds';

export const FeaturedResidences = () => {
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    const step = card ? card.getBoundingClientRect().width + 24 : 400;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section
      id="residences"
      data-testid={RESIDENCES.root}
      className="relative bg-[#F5F1E8] py-24 lg:py-32"
    >
      <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[#C9A458]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                Featured Homes
              </span>
            </div>
            <h2 className="font-serif text-[#18342A] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light max-w-2xl">
              Two homes.
              <br />
              <em className="not-italic text-[#C9A458]">One nature</em>
              <br />
              crafted land.
            </h2>
          </motion.div>

          <div className="flex items-center gap-3 md:self-end">
            <button
              type="button"
              data-testid={RESIDENCES.prev}
              onClick={() => scrollBy(-1)}
              aria-label="Previous residence"
              className="w-12 h-12 grid place-items-center border border-[#18342A]/30 text-[#18342A] hover:bg-[#18342A] hover:text-[#F5F1E8] hover:border-[#18342A] transition-colors"
            >
              <ChevronLeft size={18} strokeWidth={1.4} />
            </button>
            <button
              type="button"
              data-testid={RESIDENCES.next}
              onClick={() => scrollBy(1)}
              aria-label="Next residence"
              className="w-12 h-12 grid place-items-center border border-[#18342A]/30 text-[#18342A] hover:bg-[#18342A] hover:text-[#F5F1E8] hover:border-[#18342A] transition-colors"
            >
              <ChevronRight size={18} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid={RESIDENCES.track}
        className="no-scrollbar overflow-x-auto scroll-smooth pl-6 md:pl-10 lg:pl-16 pr-6"
      >
        <div className="flex gap-6 lg:gap-8 pb-4">
          {DATA.map((r, i) => (
            <motion.article
              key={r.id}
              data-card
              data-testid={RESIDENCES.card(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group relative shrink-0 w-[78vw] sm:w-[58vw] md:w-[44vw] lg:w-[32vw] xl:w-[26vw]"
            >
              <div className="relative overflow-hidden bg-[#18342A]/5">
                <Link to={`/residences/${r.slug}`} aria-label={`Explore ${r.name}`}>
                  <img
                    src={r.image}
                    alt={r.name}
                    className="w-full aspect-[4/5] object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                </Link>
                <div className="absolute top-4 left-4">
                  <span className="bg-[#F5F1E8]/85 backdrop-blur-md text-[#18342A] text-[10px] tracking-[0.3em] uppercase px-3 py-1.5">
                    {r.status}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-[#18342A]/85 to-transparent text-[#F5F1E8]">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C9A458] mb-2">
                    <MapPin size={12} strokeWidth={1.5} />
                    {r.location}
                  </div>
                  <Link to={`/residences/${r.slug}`}>
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight hover:text-[#C9A458] transition-colors">{r.name}</h3>
                  </Link>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-6 text-[#6D6A61]">
                  <Spec label="Beds" value={r.beds} />
                  <span className="w-px h-4 bg-[#6D6A61]/30" />
                  <Spec label="Baths" value={r.baths} />
                  <span className="w-px h-4 bg-[#6D6A61]/30" />
                  <Spec label="Acres" value={r.acres} />
                </div>
                <Link
                  to={`/residences/${r.slug}`}
                  aria-label={`View ${r.name}`}
                  className="w-10 h-10 grid place-items-center border border-[#18342A]/30 text-[#18342A] hover:bg-[#C9A458] hover:border-[#C9A458] hover:text-[#18342A] transition-colors"
                >
                  <ArrowUpRight size={16} strokeWidth={1.4} />
                </Link>
              </div>
            </motion.article>
          ))}
          <div className="shrink-0 w-2" />
        </div>
      </div>
    </section>
  );
};

const Spec = ({ label, value }) => (
  <div className="flex flex-col items-start">
    <span className="font-serif text-[#18342A] text-xl leading-none">{value}</span>
    <span className="mt-1 text-[9px] tracking-[0.35em] uppercase text-[#6D6A61]">{label}</span>
  </div>
);

export default FeaturedResidences;
